import { useEffect, useMemo } from "react"
import { Link } from "@inertiajs/react"
import { IconBluetooth, IconChevronRight } from "@tabler/icons-react"
import { Card } from "@/components/ui/card"
import { Sparkline, type SparklinePoint } from "@/components/ui/sparkline"
import { useDeviceStatus } from "@/hooks/use-device-status"
import { formatSpecificGravity } from "@/lib/ble/parsers"
import { toReadingPoint } from "@/lib/ble/parsers"
import type { DeviceSummary } from "@/types/device"

interface DeviceSummaryCardProps {
  summary: DeviceSummary
}

const ONE_SECOND_MS = 1000
const ONE_MINUTE_MS = 60 * ONE_SECOND_MS
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS
const ONE_DAY_MS = 24 * ONE_HOUR_MS

function formatLastSeen(lastSeenAt: string | null, _tick: number): string {
  if (!lastSeenAt) return "Never"
  const diffMs = Date.now() - Date.parse(lastSeenAt)
  if (diffMs < ONE_MINUTE_MS) {
    const seconds = Math.max(1, Math.floor(diffMs / ONE_SECOND_MS))
    return seconds <= 1 ? "Just now" : `${seconds}s ago`
  }
  if (diffMs < ONE_HOUR_MS) return `${Math.floor(diffMs / ONE_MINUTE_MS)}m ago`
  if (diffMs < ONE_DAY_MS) return `${Math.floor(diffMs / ONE_HOUR_MS)}h ago`
  return `${Math.floor(diffMs / ONE_DAY_MS)}d ago`
}

export function DeviceSummaryCard({ summary }: DeviceSummaryCardProps) {
  const { device, readings } = summary
  const {
    seedHistory,
    getReadingsHistory,
    getLastSeenAt,
    isConnected,
    updateDeviceStatus,
    tick,
  } = useDeviceStatus()

  // Seed the buffer so the sparkline has content on first paint and stays
  // in sync with realtime broadcasts on the device.
  useEffect(() => {
    seedHistory(device.id, readings.map(toReadingPoint))
  }, [device.id, readings, seedHistory])

  // Bootstrap the connection state from the server-rendered last_seen_at so
  // the connection dot is correct before any ActionCable broadcast arrives.
  useEffect(() => {
    updateDeviceStatus(device.id, device.last_seen_at)
  }, [device.id, device.last_seen_at, updateDeviceStatus])

  const history = getReadingsHistory(device.id)
  const sparkData: SparklinePoint[] = useMemo(
    () => history.map((point) => ({ t: point.t, v: point.sg })),
    [history]
  )

  const latestSg = history.length > 0 ? history[history.length - 1].sg : null
  const lastSeenAt = getLastSeenAt(device.id) ?? device.last_seen_at
  const connected = isConnected(device.id)

  return (
    <Link
      href={`/devices/${device.id}`}
      prefetch="hover"
      data-slot="device-summary-card"
      className="block rounded-lg border bg-card transition-all active:scale-[0.99] active:bg-muted/40"
    >
      <Card className="border-0 shadow-none">
        <div className="flex items-start gap-3 p-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
            <IconBluetooth className="size-5 text-blue-500" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="truncate text-base font-semibold">{device.name}</h3>
              <span
                className={`size-2 shrink-0 rounded-full ${connected ? "bg-green-500" : "bg-red-500"}`}
                aria-label={connected ? "Connected" : "Disconnected"}
              />
            </div>
            <div className="mt-0.5 flex items-baseline gap-2">
              <span className="text-3xl font-medium tabular-nums leading-none">
                {formatSpecificGravity(latestSg)}
              </span>
              <span className="text-xs text-muted-foreground">
                · {formatLastSeen(lastSeenAt, tick)}
              </span>
            </div>
          </div>
          <IconChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground" />
        </div>
        <div className="px-4 pb-4">
          <Sparkline
            data={sparkData}
            ariaLabel={`Specific gravity over the last 24 hours for ${device.name}`}
            height={48}
          />
        </div>
      </Card>
    </Link>
  )
}
